import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";

const BH_OPEN = 8;
const BH_CLOSE = 17;
const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function tlNow() {
  // Dili UTC+9, no DST
  return new Date(Date.now() + 9 * 3600 * 1000);
}
function isBusinessNow() {
  const d = tlNow();
  const dow = d.getUTCDay();
  const h = d.getUTCHours();
  return dow >= 1 && dow <= 5 && h >= BH_OPEN && h < BH_CLOSE;
}
function nextOpenLabel() {
  const d = tlNow();
  let cur = new Date(d);
  cur.setUTCHours(BH_OPEN, 0, 0, 0);
  for (let i = 0; i < 8; i++) {
    const dow = cur.getUTCDay();
    if (dow >= 1 && dow <= 5 && cur.getTime() > d.getTime()) {
      const sameDay = cur.getUTCDate() === d.getUTCDate() && cur.getUTCMonth() === d.getUTCMonth();
      const dayLabel = sameDay ? "today" : DAY_LABELS[dow];
      return `${dayLabel} ${BH_OPEN}:00`;
    }
    cur = new Date(cur.getTime() + 24 * 3600 * 1000);
    cur.setUTCHours(BH_OPEN, 0, 0, 0);
  }
  return `${BH_OPEN}:00`;
}

function fmt(ms) {
  const abs = Math.abs(ms);
  const h = Math.floor(abs / 3600000);
  const m = Math.floor((abs % 3600000) / 60000);
  if (h === 0) return `${m}m`;
  return `${h}h ${m}m`;
}

export default function SLABadge({ dueAt, status, responseDueAt, firstResponseAt, compact }) {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick((x) => x + 1), 60 * 1000);
    return () => clearInterval(t);
  }, []);
  // Reference tick to force re-render — value not used directly
  void tick;

  if (!dueAt || status === "resolved" || status === "closed" || status === "cancelled") {
    return <span className="font-mono text-[11px] text-slate-500">—</span>;
  }

  const businessNow = isBusinessNow();
  const due = new Date(dueAt);
  const now = new Date();
  const diffMs = due.getTime() - now.getTime();
  const breached = diffMs < 0;
  const hoursLeft = diffMs / 3600000;

  let frBreached = false;
  if (!firstResponseAt && responseDueAt) {
    frBreached = new Date(responseDueAt).getTime() < now.getTime();
  }

  // If outside business hours AND not yet breached, show a paused indicator
  if (!businessNow && !breached) {
    return (
      <span className="inline-flex items-center gap-1.5 whitespace-nowrap" title="SLA countdown paused outside 08:00–17:00 Dili">
        <span data-testid="sla-badge" className="inline-flex items-center rounded-sm px-2 py-0.5 text-[11px] font-mono bg-slate-200 text-slate-700 border border-slate-200">
          {compact ? "Paused" : `Paused · resumes ${nextOpenLabel()}`}
        </span>
        {frBreached && (
          <span className="inline-flex items-center rounded-sm px-1.5 py-0.5 text-[10px] font-mono bg-red-500/10 text-red-400 border border-red-500/30" title="First-response SLA breached" data-testid="fr-breach-badge">FR!</span>
        )}
      </span>
    );
  }

  const label = breached ? `-${fmt(diffMs)}` : fmt(diffMs);
  let cls, text;
  if (breached) {
    cls = "bg-slate-50 text-red-400 border border-red-500/60 sla-pulse";
    text = `Breached ${label}`;
  } else if (hoursLeft < 1) {
    cls = "bg-red-500 text-white sla-pulse";
    text = `Due ${label}`;
  } else if (hoursLeft < 4) {
    cls = "bg-amber-400 text-black";
    text = `Due ${label}`;
  } else {
    cls = "bg-emerald-500/15 text-emerald-400 border border-emerald-500/30";
    text = `On time · ${label}`;
  }

  return (
    <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
      <span data-testid="sla-badge" className={cn("inline-flex items-center rounded-sm px-2 py-0.5 text-[11px] font-mono", cls)}>
        {compact ? label : text}
      </span>
      {frBreached && (
        <span className="inline-flex items-center rounded-sm px-1.5 py-0.5 text-[10px] font-mono bg-red-500/10 text-red-400 border border-red-500/30" title="First-response SLA breached" data-testid="fr-breach-badge">FR!</span>
      )}
    </span>
  );
}
